import React, { useState, useEffect, useCallback } from 'react';
import { Clock, LogIn, LogOut, Coffee, AlertCircle } from 'lucide-react';
import { useTimeTrackingWebSocket } from '@/domains/internal/staff/hooks/useTimeTrackingWebSocket';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useLayoutData } from '@/shared/contexts/LayoutDataContext';
import { logger } from '@/utils/logger';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { timeTrackingService } from '@/domains/internal/staff/services/timeTrackingService';

// Clock status type (matches what the API returns)
interface ClockStatus {
  is_clocked_in: boolean;
  clock_in_time: string | null;
  duration_hours: number | null;
  time_log_id: string | null;
}

// Extended status for UI state (includes break info from WebSocket)
interface ClockStatusWithBreaks extends ClockStatus {
  is_on_break?: boolean;
  total_break_minutes?: number;
}

const ClockInOutButton: React.FC = () => {
  const { } = useAuth(); // Currently not using auth values but keep hook for context

  // Get clock status from LayoutDataContext (fetched in parallel with other layout data)
  const { clockStatus: initialStatus, loading: contextLoading } = useLayoutData();

  const [status, setStatus] = useState<ClockStatusWithBreaks | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [errorModal, setErrorModal] = useState<{ isOpen: boolean; message: string }>({
    isOpen: false,
    message: '',
  });

  // Update local state when context provides initial status
  useEffect(() => {
    if (initialStatus && !status) {
      setStatus(initialStatus);
    }
  }, [initialStatus, status]);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await timeTrackingService.getClockStatus();
      setStatus(response);
    } catch (err: any) {
      // If 404, the endpoint doesn't exist (likely on admin portal/public schema)
      // Don't log error, just hide the component
      if (err.response?.status !== 404) {
        logger.error('[ClockInOut] Failed to fetch clock status:', err);
      }
    }
  }, []);

  // WebSocket real-time updates for current user's status
  const handleWebSocketMessage = useCallback((message: any) => {
    // Get current user ID from session
    const userStr = localStorage.getItem('user');
    if (!userStr) return;

    try {
      const user = JSON.parse(userStr);
      const currentUserId = user.staff_id || user.user_id;

      // Only update if this message is for the current user
      if (message.data.staff_id === currentUserId) {
        setStatus((prevStatus) => {
          if (!prevStatus) return prevStatus;

          return {
            ...prevStatus,
            is_on_break: message.data.is_on_break,
            total_break_minutes: message.data.total_break_minutes || prevStatus.total_break_minutes || 0,
          };
        });

        // Fetch full status to get updated duration_hours
        fetchStatus();
      }
    } catch (error) {
      logger.error('Failed to parse user data:', error);
    }
  }, [fetchStatus]);

  useTimeTrackingWebSocket({
    onMessage: handleWebSocketMessage,
  });

  const showError = (message: string) => {
    setErrorModal({ isOpen: true, message });
  };

  const handleClockIn = async () => {
    setActionLoading(true);
    try {
      await timeTrackingService.clockIn();
      await fetchStatus();
      setShowDropdown(false);
    } catch (err: any) {
      logger.error('Failed to clock in:', err);
      showError(err.response?.data?.error || 'Failed to clock in');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    setActionLoading(true);
    try {
      await timeTrackingService.clockOut();
      await fetchStatus();
      setShowDropdown(false);
    } catch (err: any) {
      logger.error('Failed to clock out:', err);
      showError(err.response?.data?.error || 'Failed to clock out');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartBreak = async () => {
    setActionLoading(true);
    try {
      await timeTrackingService.startBreak();
      await fetchStatus();
    } catch (err: any) {
      logger.error('Failed to start break:', err);
      showError(err.response?.data?.error || 'Failed to start break');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEndBreak = async () => {
    setActionLoading(true);
    try {
      await timeTrackingService.endBreak();
      await fetchStatus();
    } catch (err: any) {
      logger.error('Failed to end break:', err);
      showError(err.response?.data?.error || 'Failed to end break');
    } finally {
      setActionLoading(false);
    }
  };

  const formatClockedInTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (contextLoading) {
    return (
      <div className="flex items-center px-3 py-2 rounded-md bg-gray-100 animate-pulse">
        <Clock className="w-4 h-4 text-gray-400" />
      </div>
    );
  }

  return (
    <div className="relative">
      <Button
        onClick={() => setShowDropdown(!showDropdown)}
        variant="neutral"
        padding="px-3 py-2"
        className={`flex items-center gap-2 ${
          status?.is_clocked_in
            ? status.is_on_break
              ? '!bg-amber-50 !text-amber-700 hover:!bg-amber-100'
              : '!bg-green-50 !text-green-700 hover:!bg-green-100'
            : '!bg-gray-100 !text-gray-700 hover:!bg-gray-200'
        }`}
      >
        <Clock className="w-4 h-4" />
        <span className="text-sm font-medium hidden sm:inline">
          {status?.is_clocked_in ? `${(status.duration_hours ?? 0).toFixed(1)}h` : 'Clock In'}
        </span>
        {status?.is_clocked_in && (
          <div className={`w-2 h-2 rounded-full animate-pulse ${
            status.is_on_break ? 'bg-amber-500' : 'bg-green-500'
          }`}></div>
        )}
      </Button>

      {showDropdown && (
        <>
          {/* eslint-disable-next-line no-restricted-syntax */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowDropdown(false)}
          ></div>
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-indigo-600" />
                <h3 className="font-semibold text-gray-900">Time Clock</h3>
              </div>

              {status?.is_clocked_in ? (
                <>
                  <div className={`mb-3 p-3 rounded-md ${
                    status.is_on_break
                      ? 'bg-amber-50 border border-amber-200'
                      : 'bg-green-50 border border-green-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-2 h-2 rounded-full animate-pulse ${
                        status.is_on_break ? 'bg-amber-500' : 'bg-green-500'
                      }`}></div>
                      <span className={`text-sm font-medium ${
                        status.is_on_break ? 'text-amber-800' : 'text-green-800'
                      }`}>
                        {status.is_on_break ? 'On Break' : 'Clocked In'}
                      </span>
                    </div>
                    <div className={`text-xs space-y-1 ${
                      status.is_on_break ? 'text-amber-700' : 'text-green-700'
                    }`}>
                      <div className="flex justify-between">
                        <span>Started:</span>
                        <span className="font-medium">
                          {status.clock_in_time && formatClockedInTime(status.clock_in_time)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Hours:</span>
                        <span className="font-medium">{(status.duration_hours ?? 0).toFixed(2)} hrs</span>
                      </div>
                      {(status.total_break_minutes ?? 0) > 0 && (
                        <div className="flex justify-between">
                          <span>Break:</span>
                          <span className="font-medium">{(status.total_break_minutes ?? 0).toFixed(0)} min</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Break Controls */}
                  <div className="mb-3">
                    {status.is_on_break ? (
                      <Button
                        onClick={handleEndBreak}
                        disabled={actionLoading}
                        variant="neutral"
                        fullWidth
                        padding="px-4 py-2"
                        fontSize="text-sm"
                        className="flex items-center justify-center gap-2 !bg-amber-600 !text-white hover:!bg-amber-700"
                      >
                        <Coffee className="w-4 h-4" />
                        {actionLoading ? 'Ending Break...' : 'End Break'}
                      </Button>
                    ) : (
                      <Button
                        onClick={handleStartBreak}
                        disabled={actionLoading}
                        variant="neutral"
                        fullWidth
                        padding="px-4 py-2"
                        fontSize="text-sm"
                        className="flex items-center justify-center gap-2 !bg-amber-500 !text-white hover:!bg-amber-600"
                      >
                        <Coffee className="w-4 h-4" />
                        {actionLoading ? 'Starting Break...' : 'Start Break'}
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    <span>Not clocked in</span>
                  </div>
                </div>
              )}

              <Button
                onClick={status?.is_clocked_in ? handleClockOut : handleClockIn}
                disabled={actionLoading}
                variant={status?.is_clocked_in ? 'danger' : 'info'}
                fullWidth
                padding="px-4 py-2"
                className={`flex items-center justify-center gap-2 ${
                  !status?.is_clocked_in ? '!bg-indigo-600 hover:!bg-indigo-700' : ''
                }`}
              >
                {status?.is_clocked_in ? (
                  <>
                    <LogOut className="w-4 h-4" />
                    {actionLoading ? 'Clocking Out...' : 'Clock Out'}
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4" />
                    {actionLoading ? 'Clocking In...' : 'Clock In'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
        title="Error"
        size="sm"
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-700">{errorModal.message}</p>
        </div>
      </Modal>
    </div>
  );
};

export default ClockInOutButton;
